import { getTheme, IComboBoxOption } from '@fluentui/react';
import { HorizontalBarChart, IChartDataPoint, IChartProps } from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPCheckbox, IPSpinner, IPText } from 'baseComponents';
import { IPComboBox } from 'baseComponents/Combobox/IPComboBox';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { useWidgetDrillDownDisabled } from 'components/Dashboard/WidgetDrillDownContext';
import { globalStackTokensGapMedium } from 'globalStyles';
import { AzureSubscription } from 'models/azure/azureSubscription';
import { Widget59ResultDTO } from 'models/dto/widgets/widget59DTO';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAzureSubscriptions } from 'services/Api/azureService';
import { apiGetWidgetDataSecureScores } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { newGuidNil } from 'utils/guid';
import { navigateToExternalUrl } from 'utils/url';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetMicrosoftSecureScoresConfig {
  id: string | undefined;

  name: string | undefined;

  microsoftCloudSecureScore: boolean;

  microsoft365SecureScore: boolean;

  microsoftDevicesSecureScore: boolean;

  microsoftIdentitySecureScore: boolean;

  constructor() {
    this.id = '';
    this.name = '';
    this.microsoftCloudSecureScore = true;
    this.microsoft365SecureScore = false;
    this.microsoftDevicesSecureScore = false;
    this.microsoftIdentitySecureScore = false;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.id = newRawConfig.id ?? '';
        this.name = newRawConfig.name ?? '';
        this.microsoftCloudSecureScore = newRawConfig.microsoftCloudSecureScore ?? true;
        this.microsoft365SecureScore = newRawConfig.microsoft365SecureScore ?? false;
        this.microsoftDevicesSecureScore = newRawConfig.microsoftDevicesSecureScore ?? false;
        this.microsoftIdentitySecureScore = newRawConfig.microsoftIdentitySecureScore ?? false;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetMicrosoftSecureScoresConfig {
    const newConfig = new WidgetMicrosoftSecureScoresConfig();
    newConfig.id = this.id;
    newConfig.name = this.name;
    newConfig.microsoftCloudSecureScore = this.microsoftCloudSecureScore;
    newConfig.microsoft365SecureScore = this.microsoft365SecureScore;
    newConfig.microsoftDevicesSecureScore = this.microsoftDevicesSecureScore;
    newConfig.microsoftIdentitySecureScore = this.microsoftIdentitySecureScore;

    return newConfig;
  }
}

interface IWidgetMicrosoftSecureScoresProps extends IWidgetRendererProps {}

const WidgetMicrosoftSecureScores = (props: IWidgetMicrosoftSecureScoresProps) => {
  const { t } = useTranslation(['translation', 'widgets', 'adminCompany']);
  const appContext = useContext(AppContext);
  const [data, setData] = useState<Widget59ResultDTO | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [config, setConfig] = useState<WidgetMicrosoftSecureScoresConfig | undefined>(undefined);
  const theme = getTheme();
  const drillDownDisabled = useWidgetDrillDownDisabled();

  //on a per-customer (Tenant-type) dashboard these open the partner's own Microsoft portal (no customer-specific URL exists), so disable them
  const getScoreOnClick = (url: string) =>
    drillDownDisabled ? undefined : () => navigateToExternalUrl(url, undefined, '', true);

  useEffect(() => {
    const loadConfig = (): WidgetMicrosoftSecureScoresConfig => {
      const newConfig = new WidgetMicrosoftSecureScoresConfig();
      newConfig.load(props.widget.widgetConfig);

      return newConfig;
    };

    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        const config = loadConfig();
        setConfig(config);

        if (config) {
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);

          const data = await apiGetWidgetDataSecureScores(
            accessToken,
            config.id,
            config.microsoft365SecureScore,
            config.microsoftCloudSecureScore,
            config.microsoftDevicesSecureScore,
            config.microsoftIdentitySecureScore,
            props.abortSignal,
          );

          setData(data);
        }
      } catch {
        //ignore errors, user has no auth probably
        setData(undefined);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const getData = (): IChartProps[] => {
    const dataPoints: IChartProps[] = [];
    if (!data) {
      return dataPoints;
    }

    if (
      config?.microsoft365SecureScore &&
      data.microsoft365SecureScore !== undefined &&
      data.microsoft365SecureScore >= 0
    ) {
      const title = t('adminCompany:Tabs.Integration.SecureScores.Microsoft_365_Secure_Score');
      const newPoint: IChartProps = {
        chartTitle: title,
        chartData: [
          {
            legend: title,
            horizontalBarChartdata: { x: data.microsoft365SecureScore ?? 0, y: 100 },
            color: theme.palette.green,
            yAxisCalloutData: `${data.microsoft365SecureScore ?? 0}%`,
            onClick: getScoreOnClick('https://security.microsoft.com/securescore'),
          },
        ],
      };

      dataPoints.push(newPoint);
    }

    if (
      config?.microsoftCloudSecureScore &&
      data.microsoftCloudSecureScore !== undefined &&
      data.microsoftCloudSecureScore >= 0
    ) {
      const title = t('adminCompany:Tabs.Integration.SecureScores.Microsoft_Cloud_Secure_Score');
      const newPoint: IChartProps = {
        chartTitle: title,
        chartData: [
          {
            legend: title,
            horizontalBarChartdata: { x: data.microsoftCloudSecureScore ?? 0, y: 100 },
            color: theme.palette.green,
            yAxisCalloutData: `${data.microsoftCloudSecureScore ?? 0}%`,
            onClick: getScoreOnClick('https://portal.azure.com/#blade/Microsoft_Azure_Security/SecurityMenuBlade/23'),
          },
        ],
      };

      dataPoints.push(newPoint);
    }

    if (
      config?.microsoftIdentitySecureScore &&
      data.microsoftIdentitySecureScore !== undefined &&
      data.microsoftIdentitySecureScore >= 0
    ) {
      const title = t('adminCompany:Tabs.Integration.SecureScores.Microsoft_Identity_Secure_Score');
      const newPoint: IChartProps = {
        chartTitle: title,
        chartData: [
          {
            legend: title,
            horizontalBarChartdata: { x: data.microsoftIdentitySecureScore ?? 0, y: 100 },
            color: theme.palette.green,
            yAxisCalloutData: `${data.microsoftIdentitySecureScore ?? 0}%`,
            onClick: getScoreOnClick(
              'https://entra.microsoft.com/#view/Microsoft_AAD_IAM/SecurityMenuBlade/~/IdentitySecureScore',
            ),
          },
        ],
      };

      dataPoints.push(newPoint);
    }

    if (
      config?.microsoftDevicesSecureScore &&
      data.microsoftDevicesSecureScore !== undefined &&
      data.microsoftDevicesSecureScore >= 0
    ) {
      const title = t('adminCompany:Tabs.Integration.SecureScores.Microsoft_Devices_Secure_Score');
      const newPoint: IChartProps = {
        chartTitle: title,
        chartData: [
          {
            legend: title,
            horizontalBarChartdata: { x: data.microsoftDevicesSecureScore ?? 0, y: 100 },
            color: theme.palette.green,
            yAxisCalloutData: `${data.microsoftDevicesSecureScore ?? 0}%`,
            onClick: getScoreOnClick('https://security.microsoft.com/exposure-recommendations?tabId=Devices'),
          },
        ],
      };

      dataPoints.push(newPoint);
    }

    if (
      config?.microsoftDevicesSecureScore &&
      data.microsoftDevicesExposureScore !== undefined &&
      data.microsoftDevicesExposureScore >= 0
    ) {
      const title = t('adminCompany:Tabs.Integration.SecureScores.Microsoft_Devices_Exposure_Score');
      const newPoint: IChartProps = {
        chartTitle: title,
        chartData: [
          {
            legend: title,
            horizontalBarChartdata: { x: data.microsoftDevicesExposureScore ?? 0, y: 100 },
            color: theme.palette.red,
            onClick: getScoreOnClick('https://security.microsoft.com/tvm_dashboard/'),
            placeHolder: false,
          },
        ],
      };

      dataPoints.push(newPoint);
    }

    return dataPoints;
  };

  const onRenderCustomData = (props1?: IChartProps, defaultRender?: (props?: IChartProps) => JSX.Element | null) => {
    const chartData: IChartDataPoint = props1!.chartData![0];

    const x = chartData.horizontalBarChartdata!.x;
    const y = chartData.horizontalBarChartdata!.y;

    switch (chartData.legend) {
      case t('adminCompany:Tabs.Integration.SecureScores.Microsoft_Devices_Exposure_Score'):
        return (
          <div>
            <span style={{ fontWeight: 'bold' }}>{x}</span>
            <span>{`/${y}`}</span>
          </div>
        );
      default:
        return (
          <div>
            <span style={{ fontWeight: 'bold' }}>{`${x}%`}</span>
          </div>
        );
    }
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  const dataPoints = getData();

  if (dataPoints.length === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:MicrosoftSecureScores.NoData')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill horizontalAlign="center" tokens={globalStackTokensGapMedium}>
      <IPStack.Item grow styles={{ root: { paddingTop: 20, paddingBottom: 20, paddingLeft: 5, paddingRight: 10 } }}>
        <HorizontalBarChart
          data={dataPoints}
          hideRatio={[true, false]}
          roundCorners
          barChartCustomData={onRenderCustomData}
          chartDataMode="percentage"
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetMicrosoftSecureScores;

//
// Config
//

interface IWidgetConfigMicrosoftSecureScoresProps extends IWidgetConfigRendererProps {}

export const WidgetConfigMicrosoftSecureScores = (props: IWidgetConfigMicrosoftSecureScoresProps) => {
  const { t } = useTranslation(['translation', 'widgets', 'adminCompany']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [azureSubscriptions, setAzureSubscriptions] = useState<AzureSubscription[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<AzureSubscription | undefined>(undefined);
  const [microsoftCloudSecureScoreEnabled, setMicrosoftCloudSecureScoreEnabled] = useState<boolean>(false);
  const [microsoft365SecureScoreEnabled, setMicrosoft365SecureScoreEnabled] = useState<boolean>(false);
  const [microsoftIdentitySecureScoreEnabled, setMicrosoftIdentitySecureScoreEnabled] = useState<boolean>(false);
  const [microsoftDevicesSecureScoreEnabled, setMicrosoftDevicesSecureScoreEnabled] = useState<boolean>(false);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetMicrosoftSecureScoresConfig => {
    let newRawConfig: WidgetMicrosoftSecureScoresConfig = new WidgetMicrosoftSecureScoresConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
    }

    return newRawConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const config = loadConfig();

      setMicrosoftCloudSecureScoreEnabled(config.microsoftCloudSecureScore);
      setMicrosoft365SecureScoreEnabled(config.microsoft365SecureScore);
      setMicrosoftDevicesSecureScoreEnabled(config.microsoftDevicesSecureScore);
      setMicrosoftIdentitySecureScoreEnabled(config.microsoftIdentitySecureScore);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const azureSubscriptions = await apiGetAzureSubscriptions(accessToken);

      setAzureSubscriptions(azureSubscriptions);

      if (config.id) {
        const sub = azureSubscriptions.find((e) => e.id === config.id);
        setSelectedSubscription(sub);
      }
    } catch {
      //ignore errors
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (
    id: string,
    name: string,
    microsoft365SecureScore: boolean,
    microsoftCloudSecureScore: boolean,
    microsoftDevicesSecureScore: boolean,
    microsoftIdentitySecureScore: boolean,
  ): WidgetMicrosoftSecureScoresConfig => {
    const config = new WidgetMicrosoftSecureScoresConfig();
    config.id = id;
    config.name = name;
    config.microsoft365SecureScore = microsoft365SecureScore;
    config.microsoftCloudSecureScore = microsoftCloudSecureScore;
    config.microsoftDevicesSecureScore = microsoftDevicesSecureScore;
    config.microsoftIdentitySecureScore = microsoftIdentitySecureScore;

    return config;
  };

  const updateConfig = (
    subscriptionId: string,
    microsoft365SecureScore: boolean,
    microsoftCloudSecureScore: boolean,
    microsoftDevicesSecureScore: boolean,
    microsoftIdentitySecureScore: boolean,
  ) => {
    const subscriptionName = azureSubscriptions.find((a) => a.id === subscriptionId)?.displayName;
    const config = getConfig(
      subscriptionId,
      subscriptionName || '',
      microsoft365SecureScore,
      microsoftCloudSecureScore,
      microsoftDevicesSecureScore,
      microsoftIdentitySecureScore,
    );
    props.onUpdateConfig(JSON.stringify(config), isConfigValid(config));
  };

  const isConfigValid = (config: WidgetMicrosoftSecureScoresConfig): boolean => {
    //when cloud score is selected, subscription id is required
    if (config.microsoftCloudSecureScore && config.id === '') {
      return false;
    }

    //at least one score type must be selected
    return (
      config.microsoft365SecureScore ||
      config.microsoftCloudSecureScore ||
      config.microsoftDevicesSecureScore ||
      config.microsoftIdentitySecureScore
    );
  };

  const getOptions = (): IComboBoxOption[] => {
    let items: IComboBoxOption[] = [];

    items = azureSubscriptions.map((sub) => {
      return {
        key: sub.id,
        text: sub.displayName,
      };
    });

    if (items.length === 0) {
      items.push({
        key: newGuidNil(),
        text: t('widgets:MicrosoftSecureScores.Config.Subscription.NoneFound'),
      });
    }

    return items;
  };

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPCheckbox
          label={t('adminCompany:Tabs.Integration.SecureScores.Microsoft_365_Secure_Score')}
          checked={microsoft365SecureScoreEnabled}
          onChange={(ev, checked) => {
            setMicrosoft365SecureScoreEnabled(checked ?? false);
            updateConfig(
              selectedSubscription?.id ?? '',
              checked ?? false,
              microsoftCloudSecureScoreEnabled,
              microsoftDevicesSecureScoreEnabled,
              microsoftIdentitySecureScoreEnabled,
            );
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          label={t('adminCompany:Tabs.Integration.SecureScores.Microsoft_Identity_Secure_Score')}
          checked={microsoftIdentitySecureScoreEnabled}
          onChange={(ev, checked) => {
            setMicrosoftIdentitySecureScoreEnabled(checked ?? false);
            updateConfig(
              selectedSubscription?.id ?? '',
              microsoft365SecureScoreEnabled,
              microsoftCloudSecureScoreEnabled,
              microsoftDevicesSecureScoreEnabled,
              checked ?? false,
            );
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          label={t('adminCompany:Tabs.Integration.SecureScores.Microsoft_Devices_Secure_Score')}
          checked={microsoftDevicesSecureScoreEnabled}
          onChange={(ev, checked) => {
            setMicrosoftDevicesSecureScoreEnabled(checked ?? false);
            updateConfig(
              selectedSubscription?.id ?? '',
              microsoft365SecureScoreEnabled,
              microsoftCloudSecureScoreEnabled,
              checked ?? false,
              microsoftIdentitySecureScoreEnabled,
            );
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          label={t('adminCompany:Tabs.Integration.SecureScores.Microsoft_Cloud_Secure_Score')}
          checked={microsoftCloudSecureScoreEnabled}
          onChange={(ev, checked) => {
            setMicrosoftCloudSecureScoreEnabled(checked ?? false);
            updateConfig(
              selectedSubscription?.id ?? '',
              microsoft365SecureScoreEnabled,
              checked ?? false,
              microsoftDevicesSecureScoreEnabled,
              microsoftIdentitySecureScoreEnabled,
            );
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPComboBox
          selectedKey={selectedSubscription?.id}
          options={getOptions()}
          label={t('widgets:MicrosoftSecureScores.Config.Subscription.Label')}
          useComboBoxAsMenuWidth
          disabled={isLoading || microsoftCloudSecureScoreEnabled === false}
          onChange={(ev, option?: IComboBoxOption | undefined) => {
            if (option && option.key !== newGuidNil()) {
              setSelectedSubscription(azureSubscriptions.find((sub) => sub.id === (option.key as string)));
              updateConfig(
                option.key as string,
                microsoft365SecureScoreEnabled,
                microsoftCloudSecureScoreEnabled,
                microsoftDevicesSecureScoreEnabled,
                microsoftIdentitySecureScoreEnabled,
              );
            }
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};
